import { put, call, all, takeEvery, select } from "redux-saga/effects";
import _ from "lodash";
import { VIEWER, ENDPOINT } from "../constants";
import LocalStorageHelper from "../utils/helper/localstorage_helper";
import { setPageTopLoader, setViewerImageData } from "../actions";
import { getDataFromKpiDash } from "../api";
import AngleConfig from "../config/AngleConfig";

const S3_BUCKET_BASE_URL = process.env.REACT_APP_S3_BUCKET_BASE_URL;

export const startDate = (state) => state.app.dateRange[0];
export const endDate = (state) => state.app.dateRange[1];
export const selectedMissionLabel = (state) => state.app.selectedMissionLabel;

const keywords = [
  "ぬまづの宝",
  "防災",
  "観光",
  "聖地巡礼",
  "ピクトレ",
  "当別町",
  "苫前町",
  "体験ミッション",
  "にいがた",
  "国際通り",
  "島めぐり",
  "那覇市",
  "SHIBUYA GAMES",
];

export function* handleImageDataByFileName(actions) {
  try {
    yield put(setPageTopLoader(true));
    const missionSelectedName = yield select(selectedMissionLabel);
    const isSpecialMission =
      !!missionSelectedName &&
      keywords.some((keyword) => missionSelectedName.includes(keyword));

    const companyName = LocalStorageHelper.getCompany();
    const cleanFileName = actions.payload.includes(".jpg")
      ? actions.payload.replace(".jpg", "")
      : actions.payload.includes(".webp") ? actions.payload.replace(".webp", "") : actions.payload;

    let fileName = actions.payload.includes(".webp") ? `${cleanFileName}.webp` : `${cleanFileName}.jpg`;
    fileName = fileName.replace("本柱 支柱", "本柱＋支柱");
    const safeFileName = fileName.replace(/'/g, "''");

    const env = process.env.REACT_APP_ENV;
    let companyTableName = `power_asset_detail_of_tepco`;
    if (companyName === "ntt") {
      companyTableName = `power_asset_detail_of_ntt_me`;
    } else if (companyName === "ntt-ft") {
      companyTableName = env === "dev" ? `power_asset_detail_of_ntt_me` : `power_asset_detail_of_ntt_ft`;
    } else if (companyName === "okiden") {
      companyTableName = `power_asset_detail_of_ntt_ft`;
    } else if (companyName === "hokuden") {
      companyTableName = `power_asset_detail_of_hepco`;
    }

    const joinTableAdjustment = companyTableName;

    //URLからseason不明を判明
    const sqlGetPpa = {
      query: `
        SELECT
          ppa.id AS ppa_id,
          ppa.power_asset_id,
          ppa.player_id,
          ppa.game_space_id,
          ppa.file_name,
          ppa.object_key
        FROM photo_of_power_asset ppa
        WHERE ppa.file_name = '${safeFileName}'
        ORDER BY ppa.id DESC
        LIMIT 1;
      `,
    };

    const [ppaTemp] = yield all([
      call(getDataFromKpiDash, sqlGetPpa, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    const ppaRow = JSON.parse(ppaTemp.body)?.[0];
    if (!ppaRow) {
      yield put(setViewerImageData([]));
      yield put(setPageTopLoader(false));
      return;
    }

    // review_request_photo_bind ではなく review_request から review_request_id を引く
    const sqlGetReviewRequest = {
      query: `
        SELECT rr.id AS review_request_id
        FROM review_request rr
        JOIN review_request_event rre
          ON rre.review_request_id = rr.id
        WHERE rr.power_asset_id = '${ppaRow.power_asset_id}'
          AND rr.player_id = '${ppaRow.player_id}'
          AND rr.game_space_id = '${ppaRow.game_space_id}'
          AND rre.type = 'APPLIED'
        ORDER BY rre.occurred_datetime DESC
        LIMIT 1;
      `,
    };

    let reviewRequestId;
    try {
      const [reviewRequestTemp] = yield all([
        call(getDataFromKpiDash, sqlGetReviewRequest, ENDPOINT.GET_DATA_API_REPLICA),
      ]);
      const reviewRequestRow = JSON.parse(reviewRequestTemp.body)?.[0];
      reviewRequestId = reviewRequestRow?.review_request_id;
    } catch (_error) {
      reviewRequestId = undefined;
    }

    // Query step1: Get review_request_id by a param file name
    // ---------- Query step1: review_request_id 取得 ----------
    // ★ 特殊ミッションの場合は padtep を SELECT / JOIN から除外
    const padtepSelectFragment = isSpecialMission ? "" : ", padtep.*";
    const padtepJoinFragment = isSpecialMission
      ? ""
      : `
        JOIN ${joinTableAdjustment} padtep
          ON pae.power_asset_id = padtep.power_asset_id
      `;

    const reviewRequestIdSelectValue = reviewRequestId
      ? `'${reviewRequestId}'`
      : "NULL";
    const sqlQuery1 = {
      query: `
        SELECT
            ppa.*,
            gs.*,
            gt.*,
            pae.occurred_datetime AS occurred_datetime,
            CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
            ps.*${padtepSelectFragment},
            ${reviewRequestIdSelectValue} AS review_request_id,
            pa.*
        FROM photo_of_power_asset ppa
        JOIN power_asset_event pae
          ON pae.power_asset_id = ppa.power_asset_id
        AND pae.player_id      = ppa.player_id
        AND pae.game_space_id  = ppa.game_space_id
        JOIN game_space gs ON gs.id = ppa.game_space_id
        JOIN game_term gt ON gt.id = gs.game_term_id
        JOIN player_state ps ON ps.player_id = ppa.player_id
        ${padtepJoinFragment}
        JOIN power_asset pa ON pa.id = ppa.power_asset_id
        WHERE
          pae.type = 'PHOTOGRAPH'
          AND ppa.file_name = '${safeFileName}'
        ORDER BY pae.occurred_datetime DESC
        LIMIT 1
      `,
    };

    const [imageReviewDataTemp] = yield all([
      call(getDataFromKpiDash, sqlQuery1, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    const imageReviewData = JSON.parse(imageReviewDataTemp.body) || [];
    if (!imageReviewData.length) {
      console.log("No imageReviewData for fileName:", fileName);
      yield put(setViewerImageData([]));
      yield put(setPageTopLoader(false));
      return;
    }

    // ---------- Query step2: 新構成の review_request_info 系から画像群を取得 ----------
    const buildSqlQuery2 = (withReviewRequestFilter) => {
      const reviewRequestFilterQuery =
        withReviewRequestFilter && reviewRequestId
          ? `AND rrrrib.review_request_id = '${reviewRequestId}'`
          : ``;
      return {
        query: `
          WITH target AS (
            SELECT
              ppa.power_asset_id,
              ppa.player_id,
              ppa.game_space_id,
              rrrrib.review_request_id,
              rre.occurred_datetime
            FROM photo_of_power_asset ppa
            JOIN review_image_info rii ON rii.photo_of_power_asset_id = ppa.id
            JOIN review_request_info_review_image_bind rririb ON rririb.review_image_info_id = rii.id
            JOIN review_request_info rri ON rri.id = rririb.review_request_info_id
            JOIN review_request_review_request_info_bind rrrrib ON rrrrib.review_request_info_id = rri.id
            JOIN review_request rr ON rr.id = rrrrib.review_request_id
            JOIN review_request_event rre
              ON rre.review_request_id = rr.id
            AND rre.type = 'APPLIED'
            WHERE ppa.file_name = '${safeFileName}'
            ${reviewRequestFilterQuery}
            ORDER BY rre.occurred_datetime DESC
            LIMIT 1
          )
          SELECT
            ppa.*,
            gs.*,
            gt.*,
            ps.*,

            ${isSpecialMission ? "" : "padtep.*,"}

            DATE_FORMAT(
              CONVERT_TZ(t.occurred_datetime, '+00:00', '+09:00'),
              '%Y-%m-%d %H:%i:%s'
            ) AS occurred_datetime_jst,
            COALESCE(papaovi.angle_of_view_name, '') AS angle_of_view_name,
            t.review_request_id AS resolved_review_request_id,
            paiwais.power_asset_id AS insulated_switch,

            pa.latitude,
            pa.longitude,
            pa.sub_type,
            pa.id AS pa_id

          FROM target t
          JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = t.power_asset_id
          AND ppa.player_id      = t.player_id
          AND ppa.game_space_id  = t.game_space_id

          JOIN review_request_review_request_info_bind rrrrib
            ON rrrrib.review_request_id = t.review_request_id
          JOIN review_request_info rri ON rri.id = rrrrib.review_request_info_id
          JOIN review_request_info_review_image_bind rririb
            ON rririb.review_request_info_id = rri.id
          JOIN review_image_info rii
            ON rii.photo_of_power_asset_id = ppa.id
          AND rririb.review_image_info_id = rii.id

          JOIN game_space gs ON gs.id = ppa.game_space_id
          JOIN game_term gt ON gt.id = gs.game_term_id
          JOIN player_state ps ON ps.player_id = ppa.player_id
          JOIN power_asset pa ON pa.id = ppa.power_asset_id
          LEFT JOIN inspection_photography_angle_of_view_info papaovi
            ON papaovi.inspection_photography_angle_of_view_id = ppa.inspection_photography_angle_of_view_id
            AND papaovi.language_code = 'JA'
          LEFT JOIN power_asset_id_with_air_insulated_switch paiwais
            ON paiwais.power_asset_id = ppa.power_asset_id
          ${isSpecialMission ? "" : `LEFT JOIN ${joinTableAdjustment} padtep ON padtep.power_asset_id = ppa.power_asset_id`}

          ORDER BY ppa.file_name ASC;
        `,
      };
    };

    let imageFilesData = [];
    const fetchImageFilesData = function* (withReviewRequestFilter) {
      const sqlQuery2 = buildSqlQuery2(withReviewRequestFilter);
      const [imageFilesDataTemp] = yield all([
        call(getDataFromKpiDash, sqlQuery2, ENDPOINT.GET_DATA_API_REPLICA),
      ]);
      return JSON.parse(imageFilesDataTemp.body || "[]");
    };

    try {
      imageFilesData = yield* fetchImageFilesData(true);
    } catch (_error) {
      imageFilesData = [];
    }

    if (!imageFilesData.length && reviewRequestId) {
      console.warn(
        "[viewerSaga] review_request_id filter returned no data, retrying without filter",
        {
          companyName,
          fileName,
          reviewRequestId,
        }
      );
      try {
        imageFilesData = yield* fetchImageFilesData(false);
      } catch (_error) {
        imageFilesData = [];
      }
    }

    // Query step3: Get all signedUrls
    let photoPathArray = [],
      photoFileNameArray = [];
    _.each(imageFilesData, function (photoData) {
      photoPathArray.push(photoData.object_key);
      photoFileNameArray.push(photoData.file_name);
    });

    _.each(imageFilesData, function (photoData, index) {
      photoData.signedUrl = `${S3_BUCKET_BASE_URL}/${encodeURIComponent(
        photoPathArray[index]
      )}`;
      photoData.review_request_id =
        photoData.resolved_review_request_id || reviewRequestId || null;
    });

    if (!imageFilesData?.length) {
      yield put(setViewerImageData([]));
      yield put(setPageTopLoader(false));
      return;
    }

    // update 0 index data from review request dateTime to photo sent dateTime
    imageFilesData[0].occurred_datetime_jst = imageReviewData[0][
      "occurred_datetime_jst"
    ]
      .slice(0, 19)
      .replace("T", " ");
    const angleNameOrder = new Map(
      (AngleConfig.angleNameSort || []).map((name, index) => [name, index])
    );
    imageFilesData = _.sortBy(
      imageFilesData,
      [
        (photoData) => {
          const orderIndex = angleNameOrder.get(photoData?.angle_of_view_name);
          return Number.isFinite(orderIndex) ? orderIndex : Number.POSITIVE_INFINITY;
        },
        (_photoData, index) => index,
      ]
    );

    yield put(setViewerImageData(imageFilesData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
    yield put(setPageTopLoader(false));
  }
}

export default function* watchMapSettings() {
  yield takeEvery(VIEWER.GET_IMAGE_DATA_BY_FILENAME, handleImageDataByFileName);
}
